import { useSpotPrices } from '@e2m-customer-portal/hooks';
import { Checkbox } from '@e2m-customer-portal/ui';
import { FormControlLabel, IconButton, Popover } from '@mui/material';
import clsx from 'clsx';
import { Interval, toDate } from 'date-fns';
import * as R from 'ramda';
import React, { ReactElement, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InfoIcon } from '../../../../assets/icons';

export interface SpotPricesControlProps {
  interval: Interval;
  checked: boolean;
  onChange: (value: boolean) => void;
}

export function SpotPricesControl({
  interval,
  checked,
  onChange,
}: Readonly<SpotPricesControlProps>): ReactElement<SpotPricesControlProps> {
  const [t] = useTranslation();
  const { data: spotPrices } = useSpotPrices({
    interval: { start: toDate(interval.start), end: toDate(interval.end) },
  });

  const [spotPricesJustUpdated, setSpotPricesJustUpdated] = useState(false);
  useEffect(() => {
    setSpotPricesJustUpdated(true);
    setTimeout(() => setSpotPricesJustUpdated(false), 5000);
  }, [spotPrices]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <section className="px-1">
      <div className="flex items-center">
        <FormControlLabel
          className="text-xs"
          sx={{
            margin: 0,
          }}
          control={<Checkbox checked={checked} onChange={(event) => onChange(event.target.checked)} />}
          label={
            <span className="select-none whitespace-nowrap text-sm leading-8">
              {t('SPOT_PRICES_CONTROL_SHOW_SPOT_PRICES')}
            </span>
          }
        />

        <div className="flex justify-center">
          <IconButton
            aria-label={t('SPOT_PRICES_CONTROL_SPOT_PRICES_INFO')}
            className="cursor-pointer text-green-200"
            onClick={handlePopoverOpen}
          >
            <InfoIcon className="w-5" />
          </IconButton>

          <Popover
            data-testid="spot-prices-control-popover"
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            open={Boolean(anchorEl)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                width: 'auto',
              },
            }}
          >
            <section className="flex max-w-prose space-x-4 p-4">
              <div className="flex flex-col space-y-2">
                {R.range(0, 12).map((n) => (
                  <div key={`heatmap-${n}`} className={`bg-heatmap-${n} size-[20px]`} />
                ))}
              </div>

              <div className="prose-sm">
                <Trans components={{ h1: <h1 className="mb-1.5 text-sm">{}</h1>, p: <p className="mt-0.5" /> }}>
                  {t('SPOT_PRICES_DESCRIPTION')}
                </Trans>
              </div>
            </section>
          </Popover>
        </div>
      </div>

      <div
        data-testid="spot-prices-last-updated"
        className={clsx(
          "relative flex items-center pl-8 text-xs leading-none text-grey-300 before:absolute before:-right-3.5 before:top-0.5 before:inline-block before:size-2 before:rounded-full before:bg-green-200 before:transition-opacity before:duration-500 before:[content:''] after:absolute after:-right-3.5 after:top-0.5 after:inline-block after:size-2 after:rounded-full after:bg-green-200 after:[content:'']",
          spotPrices && spotPricesJustUpdated ? 'after:animate-ping' : 'before:opacity-0 after:opacity-0',
        )}
      >
        {spotPrices ? t('SPOT_PRICES_LAST_UPDATED', { date: spotPrices?.lastUpdated }) : t('SHARED_LOADING_DATA')}
      </div>
    </section>
  );
}
